// src/pages/ResetPassword.js - Page réinitialisation mot de passe moderne
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import './AuthPages.css';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [sessionValid, setSessionValid] = useState(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Vérifier la session au chargement
  useEffect(() => {
    const checkSession = async () => {
      console.log('🔍 Vérification session reset password...');
      
      // 1. Vérifier s'il y a une erreur dans l'URL (query params)
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      if (error) {
        console.log('❌ Erreur dans URL:', error, errorDescription);
        if (error === 'access_denied' && errorDescription?.includes('expired')) {
          setErrorMessage('Le lien a expiré. Veuillez demander un nouveau lien.');
        } else {
          setErrorMessage(errorDescription || 'Une erreur est survenue.');
        }
        setSessionValid(false);
        return;
      }

      // 2. Vérifier code PKCE (query param) OU hash fragment (flow implicit)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const hash = window.location.hash;

      // CAS 1 — PKCE flow : ?code=xxx
      if (code) {
        console.log('🔑 Code PKCE détecté');
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error || !data.session) {
            console.log('❌ exchangeCodeForSession échoué:', error?.message);
            setErrorMessage('Lien invalide ou expiré. Veuillez demander un nouveau lien.');
            setSessionValid(false);
            return;
          }
          console.log('✅ Session valide via PKCE');
          setSessionValid(true);
          window.history.replaceState({}, document.title, '/reset-password');
        } catch (err) {
          console.log('❌ Erreur PKCE:', err.message);
          setErrorMessage('Lien invalide ou expiré. Veuillez demander un nouveau lien.');
          setSessionValid(false);
        }
        return;
      }

      // CAS 2 — Implicit flow : #access_token=xxx
      if (hash && hash.includes('access_token')) {
        console.log('🔑 Hash fragment détecté (flow implicit)');
        try {
          const params = new URLSearchParams(hash.substring(1));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          if (!accessToken) {
            setErrorMessage('Lien invalide ou expiré.');
            setSessionValid(false);
            return;
          }
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });
          if (error || !data.session) {
            setErrorMessage('Lien invalide ou expiré.');
            setSessionValid(false);
            return;
          }
          console.log('✅ Session valide via hash');
          setSessionValid(true);
          window.history.replaceState({}, document.title, '/reset-password');
        } catch (err) {
          setErrorMessage('Lien invalide ou expiré.');
          setSessionValid(false);
        }
        return;
      }

      // 3. Vérifier s'il y a une erreur dans le hash
      if (hash && hash.includes('error')) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const hashError = hashParams.get('error');
        const hashErrorDesc = hashParams.get('error_description');
        
        console.log('❌ Erreur dans hash:', hashError, hashErrorDesc);
        
        if (hashErrorDesc?.includes('expired')) {
          setErrorMessage('Le lien a expiré. Veuillez demander un nouveau lien.');
        } else {
          setErrorMessage(hashErrorDesc || 'Lien invalide.');
        }
        setSessionValid(false);
        return;
      }

      // 4. Vérifier si une session existe déjà
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        console.log('✅ Session existante trouvée');
        setSessionValid(true);
      } else {
        console.log('❌ Pas de session');
        setErrorMessage('Lien invalide ou expiré. Veuillez demander un nouveau lien.');
        setSessionValid(false);
      }
    };

    checkSession();
  }, [searchParams]);

  // Countdown après succès
  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      navigate('/login');
    }
  }, [success, countdown, navigate]);

  // Calcul de la force du mot de passe
  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;

    if (score <= 2) return { level: 'weak', text: 'Faible', bars: 1 };
    if (score <= 4) return { level: 'medium', text: 'Moyen', bars: 2 };
    return { level: 'strong', text: 'Fort', bars: 3 };
  };

  // Critères de validation
  const criteria = [
    { id: 'length', label: 'Au moins 8 caractères', valid: password.length >= 8 },
    { id: 'lowercase', label: 'Une lettre minuscule', valid: /[a-z]/.test(password) },
    { id: 'uppercase', label: 'Une lettre majuscule', valid: /[A-Z]/.test(password) },
    { id: 'number', label: 'Un chiffre', valid: /[0-9]/.test(password) },
    { id: 'special', label: 'Un caractère spécial (!@#$...)', valid: /[^a-zA-Z0-9]/.test(password), optional: true },
  ];

  const passwordStrength = getPasswordStrength(password);
  const isPasswordValid = criteria.filter(c => !c.optional).every(c => c.valid);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // Soumettre le nouveau mot de passe
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      setMessage({ type: 'error', text: 'Le mot de passe ne respecte pas les critères requis.' });
      return;
    }

    if (!passwordsMatch) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) throw error;

      setSuccess(true);
      console.log('✅ Mot de passe mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Erreur lors de la réinitialisation.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // État de chargement
  if (sessionValid === null) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>🔐 Réinitialisation</h1>
          </div>
          <div className="auth-body" style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner"></div>
            <p>Vérification en cours...</p>
          </div>
        </div>
      </div>
    );
  }

  // Lien invalide ou expiré
  if (sessionValid === false) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header error">
            <h1>❌ Lien invalide</h1>
          </div>
          <div className="auth-body" style={{ textAlign: 'center' }}>
            <p className="error-text">{errorMessage || 'Ce lien est invalide ou a expiré.'}</p>
            <p style={{ color: '#64748b', marginTop: '1rem' }}>
              💡 Demandez un nouveau lien de réinitialisation.
            </p>
            <Link to="/forgot-password" className="auth-btn" style={{ display: 'inline-block', marginTop: '1.5rem' }}>
              Demander un nouveau lien
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Succès
  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header success">
            <h1>✅ Mot de passe modifié !</h1>
          </div>
          <div className="auth-body" style={{ textAlign: 'center' }}>
            <p>Votre mot de passe a été mis à jour avec succès.</p>
            <p style={{ color: '#64748b', marginTop: '1rem' }}>
              Redirection dans <strong>{countdown}</strong> secondes...
            </p>
            <Link to="/login" className="auth-btn" style={{ display: 'inline-block', marginTop: '1.5rem' }}>
              Se connecter maintenant
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de réinitialisation
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>🔐 Nouveau mot de passe</h1>
          <p>Choisissez un mot de passe sécurisé</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-body">
          {message && (
            <div className={`auth-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Mot de passe */}
          <div className="input-group">
            <label>Nouveau mot de passe</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez votre nouveau mot de passe"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>

            {/* Indicateur de force */}
            {password && (
              <div className="password-strength">
                <div className="strength-bars">
                  {[1, 2, 3].map((bar) => (
                    <div
                      key={bar}
                      className={`bar ${bar <= passwordStrength.bars ? passwordStrength.level : ''}`}
                    />
                  ))}
                </div>
                <span className={`strength-text ${passwordStrength.level}`}>
                  {passwordStrength.text}
                </span>
              </div>
            )}

            {/* Critères */}
            {password && (
              <ul className="password-criteria">
                {criteria.map((c) => (
                  <li key={c.id} className={c.valid ? 'valid' : 'invalid'}>
                    {c.valid ? '✅' : '⬜'} {c.label} {c.optional && '(optionnel)'}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Confirmation */}
          <div className="input-group">
            <label>Confirmer le mot de passe</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmez votre mot de passe"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {confirmPassword && (
              <p className={`match-indicator ${passwordsMatch ? 'match' : 'no-match'}`}>
                {passwordsMatch ? '✅ Les mots de passe correspondent' : '❌ Les mots de passe ne correspondent pas'}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="auth-btn"
            disabled={loading || !isPasswordValid || !passwordsMatch}
          >
            {loading ? '⏳ Modification...' : '🔐 Modifier mon mot de passe'}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/login">← Retour à la connexion</Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
